import {
  Mjml,
  MjmlAll,
  MjmlAttributes,
  MjmlBody,
  MjmlHead,
} from "@faire/mjml-react";
import { genericMemo } from "@redotech/react-util/component";
import { useRequiredContext } from "@redotech/react-util/context";
import { schemas } from "@redotech/redo-model/advanced-flow/schemas/schemas";
import { EmailFormat, Section } from "@redotech/redo-model/email-builder";
import Quill from "quill";
import { useEffect, useRef } from "react";
import { FormQuillEditor } from "../../form-builder/widget-editors/form-quill-editor";
import { FormQuillEditorToolbar } from "../../form-builder/widget-editors/form-quill-editor-toolbar";
import {
  WidgetEditorDecorator,
  WidgetEditorProps,
  WidgetProps,
} from "../../form-builder/widget-editors/widget-editor-decorator";
import { WidgetElementEditor } from "../../form-builder/widget-editors/widget-element-editor";
import { convertToQuillHTML } from "../../quill/quill-editor";
import { ampScripts } from "../email-wrapper";
import { EmailBlockIsolator } from "./amp-isolator";
import { renderEmailPreview } from "./browser-mjml-renderer";
import { EmailBuilderContext } from "./email-builder-context";

/**
 * Isolates each email block in an iframe
 */
export const IsolatedWidgetElementEditor = genericMemo(
  function IframeIsolatedWidgetElementEditor<T extends WidgetProps<E>, E>(
    props: WidgetEditorDecorator<T, E>,
  ) {
    return (
      <div
        onClick={(e) => {
          props.onSelectElement(props.index, e);
          e.stopPropagation();
        }}
        style={{
          position: props.selected ? "relative" : undefined,
          zIndex: props.selected ? 999 : undefined,
          cursor: "pointer",
          outline: props.selected ? "2px solid #4D35C6" : "none",
        }}
      >
        <EmailBlockIsolator
          format={EmailFormat.STATIC} // TODO: option to preview AMP
          rawHtml={renderEmailPreview(
            <Mjml>
              <MjmlHead>
                <MjmlAttributes>
                  <MjmlAll padding="0" />
                </MjmlAttributes>
              </MjmlHead>
              <MjmlBody cssClass="mjml-body mjml-preview-body">
                {props.children(props)}
              </MjmlBody>
            </Mjml>,
            EmailFormat.STATIC, // TODO: option to preview AMP
          )}
          scriptUrls={ampScripts}
        />
        {props.sectionToolbar}
      </div>
    );
  },
);

// Every new "builder" in the app uses the same pattern but with a different generic type.
// You might be tempted to find a more generalized abstraction, but please resist unless you really
// know what you're doing. Each builder has different weird edge cases. This one (email builder)
// has to deal with isolated block rendering in iframes, which complicates rendering the section toolbar, etc.
// and slightly changing the types.
export const RichTextEditor = function RichTextEditor<
  T extends WidgetProps<Section.Text>,
>(props: WidgetEditorProps<T, Section.Text>) {
  const { element } = props.widget;

  const quillRef = useRef<Quill | null>(null);

  const containerId = `container-${element.blockId}`;
  const toolbarId = `toolbar-${element.blockId}`;

  const { template } = useRequiredContext(EmailBuilderContext);

  const schema = template ? schemas[template.schemaType] : undefined;

  const dynamicVariables = schema
    ? Object.keys(schema.fields).map((field) => field)
    : [];

  useEffect(() => {
    if (
      quillRef.current &&
      element.text &&
      quillRef.current.getSemanticHTML() !== element.text
    ) {
      quillRef.current.root.innerHTML = convertToQuillHTML(element.text);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quillRef.current, element.text]);

  return (
    <WidgetElementEditor {...props} skipRenderingSectionToolbar>
      {(subProps) => (
        <>
          <div style={{ position: "relative" }}>
            <FormQuillEditorToolbar
              dynamicVariables={dynamicVariables}
              id={toolbarId}
              open={subProps.selected}
            />
            <style>
              {`
                  #${containerId} a {
                    color: ${element.linkColor};
                  }
                  #${containerId} .ql-blank, #${containerId} .ql-editor {padding: 0px !important; }
                  #${containerId} .ql-blank::before {left: unset !important; right: unset !important; }
                  `}
            </style>
            <div
              id={containerId}
              // styles must be declared here AND in the text element,
              // since the editor uses a totally separate component for rendering
              style={{
                color: element.textColor,
                fontSize: element.fontSize,
                fontFamily: element.fontFamily,
                border: "none",
                backgroundColor: element.sectionColor,
                padding: `${element.sectionPadding.top}px ${element.sectionPadding.right}px ${element.sectionPadding.bottom}px ${element.sectionPadding.left}px`,
              }}
            >
              <FormQuillEditor
                onTextChange={(text) => {
                  if (!quillRef.current) {
                    return;
                  }
                  const html = quillRef.current.getSemanticHTML();
                  if (element.text === html) {
                    return;
                  }
                  subProps.setElement({
                    ...subProps.widget.element,
                    text: html,
                  });
                }}
                placeholder="Write here..."
                ref={quillRef}
                selector={containerId}
                toolbarElementId={toolbarId}
              />
            </div>
          </div>
          {subProps.sectionToolbar}
        </>
      )}
    </WidgetElementEditor>
  );
};
